import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Home, 
  Calendar, 
  Users, 
  Clock, 
  FileText, 
  LogOut,
  Settings,
  ChevronDown,
  ChevronRight,
  Key,
  Activity,
  BarChart3,
  Wallet,
  Sparkles,
  FileText as LogsIcon,
  Layout,
  Smartphone
} from 'lucide-react';
import { 
  baseTransitions, 
  entranceAnimations, 
  hoverAnimations, 
  navigationAnimations 
} from '../../utils/animations';
import { useAuth } from '../../contexts/AuthContext';

// Função para verificar se o usuário é desenvolvedor
const isDeveloper = (email: string | null | undefined): boolean => {
  return email === 'dev@playkids.com';
};

interface AdminLayoutProps {
  children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [developerExpanded, setDeveloperExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();

  // Verificar se o usuário ainda está autenticado
  useEffect(() => {
    const checkAuth = () => {
      // O Firebase Auth gerencia automaticamente o estado de autenticação
      // Não precisamos verificar manualmente
    };

    const interval = setInterval(checkAuth, 30000); // Verificar a cada 30 segundos
    return () => clearInterval(interval);
  }, []);



  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  const navigation = [
    { name: 'Contratos', href: '/admin/contratos', icon: FileText, badge: null },
    { name: 'Calendário', href: '/admin/calendario', icon: Calendar, badge: null },
    { name: 'Funcionários', href: '/admin/funcionarios', icon: Users, badge: null },
    { name: 'Lembretes', href: '/admin/lembretes', icon: Clock, badge: null },
    { name: 'WhatsApp', href: '/admin/whatsapp', icon: Smartphone, badge: null },
    { name: 'Estudo de Orçamentos', href: '/admin/estudo-orcamentos', icon: Wallet, badge: null },
    { name: 'Personagens', href: '/admin/personagens', icon: Sparkles, badge: null },
    { name: 'Modelo de Contrato', href: '/admin/modelo-contrato', icon: Layout, badge: null },
    { name: 'Importar', href: '/admin/importar', icon: FileText, badge: null },
  ];

  const developerMenu = [
    { name: 'Credenciais', href: '/admin/credenciais', icon: Key, badge: null },
    { name: 'Logs do Sistema', href: '/admin/logs', icon: LogsIcon, badge: null },
    { name: 'Configurações', href: '/admin/configuracoes', icon: Settings, badge: null },
    { name: 'Monitoramento', href: '/admin/monitoramento', icon: Activity, badge: null },
  ];

  const currentPath = navigation.find(item => item.href === location.pathname);

  return (
    <div className="min-h-screen bg-slate-50 transition-colors duration-200">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-teal-500 to-teal-600 shadow-2xl transform ${baseTransitions.normal} ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-teal-400/30">
          <div className="flex items-center space-x-3">
            <div className="bg-white rounded-lg p-2 shadow-lg">
              <img 
                src="/images/LOGO.png" 
                alt="PlayKids" 
                className="h-9 w-auto"
              />
            </div>
            <span className="text-xl font-bold text-white font-display">PlayKids</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className={`lg:hidden p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/20 ${baseTransitions.fast} ${hoverAnimations.scale}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="px-4 py-6 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(item.href);
                  setSidebarOpen(false);
                }}
                className={`group flex items-center px-4 py-3 text-sm font-semibold rounded-xl ${baseTransitions.normal} ${navigationAnimations.hover} ${
                  isActive
                    ? 'bg-white text-teal-700 shadow-lg scale-105'
                    : 'text-white/80 hover:bg-white/20 hover:text-white'
                }`}
              >
                <item.icon className={`w-5 h-5 mr-3 transition-colors duration-200 ${
                  isActive ? 'text-teal-700' : 'text-white/70 group-hover:text-white'
                }`} />
                {item.name}
                {item.badge && (
                  <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    {item.badge}
                  </span>
                )}
              </a>
            );
          })}

          {/* Developer Menu - Apenas para dev@playkids.com */}
          {isDeveloper(currentUser?.email) && (
            <div className="pt-4 border-t border-teal-400/30">
              <button
                onClick={() => setDeveloperExpanded(!developerExpanded)}
                className="group flex items-center w-full px-4 py-3 text-sm font-semibold text-white/80 hover:bg-white/20 hover:text-white rounded-xl transition-all duration-200"
              >
                <Settings className="w-5 h-5 mr-3 text-white/70 group-hover:text-white" />
                Desenvolvedor
                {developerExpanded ? (
                  <ChevronDown className="w-4 h-4 ml-auto" />
                ) : (
                  <ChevronRight className="w-4 h-4 ml-auto" />
                )}
              </button>
              
              {developerExpanded && (
                <div className="ml-4 mt-2 space-y-1">
                  {developerMenu.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <a
                        key={item.name}
                        href={item.href}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(item.href);
                          setSidebarOpen(false);
                        }}
                        className={`group flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-white text-teal-700 shadow-md'
                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <item.icon className={`w-4 h-4 mr-3 ${
                          isActive ? 'text-teal-700' : 'text-white/60 group-hover:text-white'
                        }`} />
                        {item.name}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-teal-400/30">
          <div className="flex items-center justify-between">
            {/* Developer Info and Contact */}
            <div className="text-xs text-white/60">
              <div>Desenvolvido por</div>
              <div className="font-medium text-white/80">Thay Ruan Oliveira</div>
              <button
                onClick={() => window.open('https://wa.me/5533998200546', '_blank')}
                className="text-white/60 hover:text-white/80 transition-colors duration-200 mt-1 block"
              >
                Entrar em contato
              </button>
            </div>
            
            {/* Divider */}
            <div className="w-px h-16 bg-white/20 mx-4"></div>
            
            {/* Logout Button */}
            <button
              onClick={() => {
                logout();
                navigate('/admin/login');
              }}
              className={`flex items-center px-3 py-2 text-sm font-medium text-white/80 hover:text-white hover:bg-white/20 rounded-lg ${baseTransitions.normal} ${hoverAnimations.scale}`}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64 relative">
        {/* Divider between sidebar and content */}
        <div className="hidden lg:block absolute left-0 top-0 bottom-0 w-px bg-black"></div>
        {/* Top Header */}
        <header className="fixed top-0 left-0 right-0 lg:relative bg-white/80 backdrop-blur-sm shadow-sm border-b border-slate-200 z-40">
          <div className="flex items-center justify-between h-20 px-4 sm:px-6 lg:px-8">
            {/* Left side */}
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`lg:hidden p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 ${baseTransitions.fast} ${hoverAnimations.scale}`}
              >
                <Menu className="w-5 h-5" />
              </button>
              
              {/* Logo PlayKids */}
              <div className="ml-4 lg:ml-0 flex items-center space-x-4">
                <img 
                  src="/images/LOGO.png" 
                  alt="PlayKids" 
                  className="h-12 w-auto"
                />
                <div className="hidden sm:block">
                  <h1 className="text-2xl font-bold text-slate-900 font-display">PlayKids</h1>
                  <p className="text-xs text-slate-600 -mt-1 font-admin">Sistema de Contratos</p>
                </div>
              </div>
              
              {/* Breadcrumb */}
              <div className="ml-6 flex items-center space-x-2 text-sm text-slate-500">
                <Home className="w-4 h-4" />
                <span>/</span>
                <span className="text-slate-900 font-semibold font-admin">
                  {currentPath?.name || 'Admin'}
                </span>
              </div>
            </div>

            {/* Right side - Company Info */}
            <div className="hidden lg:flex items-center space-x-6 text-xs text-slate-500">
              <div className="text-right">
                <div className="font-medium text-slate-600">CNPJ: 31.692.815/0001-02</div>
                <div className="text-slate-500">Marcionilia Breder Satler</div>
              </div>
              <div className="text-right">
                <div className="text-slate-600">Play Kids</div>
                <div className="text-slate-500">Ativa desde 04/10/2018</div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="relative z-0 pt-20 lg:pt-0 p-3 sm:p-4 lg:p-6">
          <div key={location.pathname} className="relative z-0">
            {children || <Outlet />}
          </div>
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className={`fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden ${entranceAnimations.fadeIn}`}
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
